import { useEffect, useState } from "react";
import { X, Bookmark, Calculator, Send, MapPin, Camera, Phone, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { errMessage } from "../lib/api";
import BlastBuyersModal from "./BlastBuyersModal";
import OutreachPanel from "./OutreachPanel";

const PLACEHOLDER =
  "https://images.unsplash.com/photo-1761347604157-3d667ff62778?crop=entropy&cs=srgb&fm=jpg&w=940&q=80";

function swapViewType(url, nextType) {
  if (!url) return url;
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set("type", nextType);
    return u.pathname + "?" + u.searchParams.toString();
  } catch {
    return url;
  }
}

const SIGNAL_LABELS = {
  pre_foreclosure: "Pre-foreclosure",
  code_violation: "Code violation",
  recent_eviction: "Recent eviction",
  tax_delinquent: "Tax delinquent",
  absentee_owner: "Absentee owner",
  vacant: "Vacant",
  probate: "Probate",
  high_equity: "High equity",
  long_term_owner: "Long-term owner",
};

export default function PropertyDetailModal({ prop, onClose, onSave }) {
  const [pct, setPct] = useState(70);
  const [busy, setBusy] = useState(false);
  const [arv, setArv] = useState(prop.estimated_arv);
  const [repairs, setRepairs] = useState(prop.estimated_repairs);
  const [fee, setFee] = useState(10000);
  const [calc, setCalc] = useState(null);
  const [showBlast, setShowBlast] = useState(false);
  const [showOutreach, setShowOutreach] = useState(false);
  const [aiSummary, setAiSummary] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [dealAnalysis, setDealAnalysis] = useState(null);
  const [view, setView] = useState("streetview");
  const heroSrc = prop.image_url ? swapViewType(prop.image_url, view) : PLACEHOLDER;
  const nextView = view === "streetview" ? "satellite" : "streetview";
  const toggleLabel = view === "streetview" ? "Aerial" : "Street";
  const ToggleIcon = view === "streetview" ? MapPin : Camera;

  useEffect(() => {
    // Auto-run the deal analyzer when modal opens (it's pure math, no API cost)
    api.post("/ai/deal-analyzer", { property_data: prop })
      .then((r) => setDealAnalysis(r.data))
      .catch(() => {});
  }, [prop]);

  const runAiSummary = async () => {
    setAiLoading(true);
    try {
      const r = await api.post("/ai/property-summary", { property_data: prop });
      setAiSummary(r.data.summary || "");
    } catch (e) {
      toast.error(errMessage(e, "AI summary failed"));
    } finally {
      setAiLoading(false);
    }
  };

  const runCalc = async () => {
    try {
      const r = await api.post("/markets/mao", {
        arv: Number(arv),
        repairs: Number(repairs),
        percentage: Number(pct) / 100,
        wholesale_fee: Number(fee),
      });
      setCalc(r.data);
    } catch (e) {
      toast.error("Could not calculate MAO");
    }
  };

  return (
    <>
    <div
      data-testid="property-detail-modal"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-[--ink] animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          <img
            src={heroSrc}
            alt="property"
            referrerPolicy="no-referrer"
            className="w-full h-52 object-cover"
            onError={(e) => {
              if (e.currentTarget.src !== PLACEHOLDER) e.currentTarget.src = PLACEHOLDER;
            }}
          />
          {prop.image_url && (
            <button
              type="button"
              data-testid="modal-view-toggle"
              aria-label={`Switch to ${toggleLabel} view`}
              onClick={() => setView(nextView)}
              className="absolute top-4 right-16 bg-white/90 hover:bg-white backdrop-blur-sm border border-[--line] px-3 py-2 text-xs font-mono font-medium uppercase tracking-wider flex items-center gap-1.5"
            >
              <ToggleIcon size={12} strokeWidth={1.5} />
              {toggleLabel}
            </button>
          )}
          <button
            data-testid="close-modal"
            onClick={onClose}
            className="absolute top-4 right-4 bg-white p-2 hover:bg-[--bg-soft] border border-[--line]"
            aria-label="Close"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
          <div className="absolute bottom-4 left-6 text-white">
            <div className="overline text-white/70 mb-1">{prop.property_type}</div>
            <div className="font-display text-2xl font-bold">{prop.address}</div>
            <div className="font-mono text-xs">{prop.city}, {prop.state} {prop.zip}</div>
          </div>
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-6">
          <div>
            <div className="overline mb-3">Property</div>
            <div className="space-y-2 text-sm">
              <Row label="Beds / Baths" v={`${prop.beds} / ${prop.baths}`} />
              <Row label="Sqft" v={prop.sqft.toLocaleString()} />
              <Row label="Year built" v={prop.year_built} />
              <Row label="Condition" v={prop.condition} />
              <Row label="Owner tenure" v={`${prop.owner_tenure_years} yrs`} />
              <Row label="Absentee owner" v={prop.absentee_owner ? "Yes" : "No"} />
            </div>

            <div className="overline mt-6 mb-2">Distress signals</div>
            <div className="flex flex-wrap gap-1.5">
              {prop.distress_signals.map((s) => (
                <span
                  key={s}
                  className="text-xs px-2 py-1 bg-red-50 text-[--danger] border border-red-200"
                >
                  {SIGNAL_LABELS[s] || s}
                </span>
              ))}
            </div>
          </div>

          <div>
            <div className="overline mb-3">Standard formulas</div>
            <div className="space-y-2 text-sm font-mono mb-6">
              <Row label="Asking" v={`$${prop.asking_price.toLocaleString()}`} />
              <Row label="ARV (est.)" v={`$${prop.estimated_arv.toLocaleString()}`} />
              <Row label="Repairs (est.)" v={`$${prop.estimated_repairs.toLocaleString()}`} />
              <Row label="MAO @ 70%" v={`$${prop.mao_70.toLocaleString()}`} bold />
              <Row label="MAO @ 75%" v={`$${prop.mao_75.toLocaleString()}`} />
              <Row label="Profit (est.)" v={`$${prop.potential_profit.toLocaleString()}`} bold />
              <Row label="Motivation" v={`${prop.motivation_score}/100`} />
            </div>

            {dealAnalysis && (
              <div className="border-t border-[--line] pt-4 mb-6" data-testid="deal-analyzer">
                <div className="overline mb-2">AI Deal Analyzer</div>
                <div className="text-xs font-mono space-y-1">
                  <Row label="Offer low (65%)" v={`$${dealAnalysis.offer_low.toLocaleString()}`} />
                  <Row label="Offer target (70%)" v={`$${dealAnalysis.offer_target.toLocaleString()}`} bold />
                  <Row label="Offer max (75%)" v={`$${dealAnalysis.offer_max.toLocaleString()}`} />
                  {dealAnalysis.spread_vs_asking != null && (
                    <Row
                      label="Spread vs asking"
                      v={`$${dealAnalysis.spread_vs_asking.toLocaleString()}`}
                    />
                  )}
                  <div className="flex justify-between pt-1">
                    <span className="text-[--ink-muted]">Verdict</span>
                    <span
                      className={`font-semibold ${
                        dealAnalysis.verdict === "Strong"
                          ? "text-green-700"
                          : dealAnalysis.verdict === "Tight"
                          ? "text-red-700"
                          : "text-yellow-700"
                      }`}
                    >
                      {dealAnalysis.verdict}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="border-t border-[--line] pt-4 mb-6">
              <div className="flex items-center justify-between mb-2">
                <div className="overline flex items-center gap-1.5">
                  <Sparkles size={12} strokeWidth={1.5} /> AI summary
                </div>
                <button
                  data-testid="ai-summary-button"
                  onClick={runAiSummary}
                  disabled={aiLoading}
                  className="text-xs text-[--accent] hover:underline"
                >
                  {aiLoading ? "Analyzing…" : aiSummary ? "Regenerate" : "Why is this a deal?"}
                </button>
              </div>
              {aiSummary && (
                <div
                  data-testid="ai-summary-text"
                  className="text-sm leading-relaxed whitespace-pre-wrap bg-[--bg-soft] p-3 border border-[--line]"
                >
                  {aiSummary}
                </div>
              )}
            </div>

            <div className="border-t border-[--line] pt-4">
              <div className="overline mb-3 flex items-center gap-1.5">
                <Calculator size={12} strokeWidth={1.5} /> Custom MAO calculator
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <label className="block">
                  <span className="text-xs text-[--ink-muted] block mb-1">ARV</span>
                  <input
                    data-testid="mao-arv"
                    type="number"
                    value={arv}
                    onChange={(e) => setArv(e.target.value)}
                    className="w-full text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-[--ink-muted] block mb-1">Repairs</span>
                  <input
                    data-testid="mao-repairs"
                    type="number"
                    value={repairs}
                    onChange={(e) => setRepairs(e.target.value)}
                    className="w-full text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-[--ink-muted] block mb-1">Percent (e.g. 70 = 70%)</span>
                  <input
                    data-testid="mao-percentage"
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                    value={pct}
                    onChange={(e) => setPct(e.target.value)}
                    className="w-full text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-[--ink-muted] block mb-1">Wholesale fee</span>
                  <input
                    data-testid="mao-fee"
                    type="number"
                    value={fee}
                    onChange={(e) => setFee(e.target.value)}
                    className="w-full text-sm"
                  />
                </label>
              </div>
              <button
                data-testid="calc-mao-button"
                onClick={runCalc}
                disabled={busy}
                className="btn-ghost w-full text-xs"
              >
                {busy ? "Calculating…" : "Calculate MAO"}
              </button>
              {calc && (
                <div data-testid="calc-mao-result" className="mt-3 p-3 bg-[--bg-soft] text-xs font-mono space-y-1">
                  <div className="text-[--ink-muted]">{calc.formula}</div>
                  <div className="font-semibold text-base text-[--accent]">
                    MAO = ${calc.mao.toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-[--line] p-4 flex justify-end gap-2 flex-wrap">
          <button
            data-testid="modal-outreach"
            onClick={() => setShowOutreach(true)}
            className="btn-ghost flex items-center gap-2"
          >
            <Phone size={14} strokeWidth={1.5} /> Contact agent
          </button>
          <button
            data-testid="modal-blast-buyers"
            onClick={() => setShowBlast(true)}
            className="btn-ghost flex items-center gap-2"
          >
            <Send size={14} strokeWidth={1.5} /> Blast to buyers
          </button>
          <button
            data-testid="modal-save-deal"
            onClick={onSave}
            className="btn-primary flex items-center gap-2"
          >
            <Bookmark size={14} strokeWidth={1.5} /> Save deal
          </button>
        </div>
      </div>
    </div>
    {showBlast && (
      <BlastBuyersModal property={prop} onClose={() => setShowBlast(false)} />
    )}
    {showOutreach && (
      <OutreachPanel property={prop} onClose={() => setShowOutreach(false)} />
    )}
    </>
  );
}

function Row({ label, v, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[--ink-muted]">{label}</span>
      <span className={bold ? "font-semibold" : ""}>{v}</span>
    </div>
  );
}
